import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { Subscription } from 'rxjs';
import {
  DataService,
  PamlabApiServiceService,
  SnackbarService,
} from '@/shared/services';
import {
  CourseCatalogAttributes,
  CourseInfoWithGradeRanges,
  TargetCourseCombined,
} from '../../../../../server/types';
import { PrerequisiteChartComponent } from '../prerequisite-chart/prerequisite-chart.component';

interface Row {
  code: string;
  name: string;
  college: string;
  prereqs?: string;
}

@Component({
  selector: 'app-prerequisites-content',
  templateUrl: './prerequisites-content.component.html',
  styleUrls: ['./prerequisites-content.component.css'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'),
      ),
    ]),
  ],
  standalone: false,
})
export class PrerequisitesContentComponent implements OnInit, OnDestroy {
  public dataPending!: boolean;
  private subscription: Subscription = new Subscription();
  public targetCrsTableData: TargetCourseCombined[] = [];
  public expandedElement!: CourseCatalogAttributes | null;
  public table: Row[] = [];
  public dataSource: Row[] = [];
  public selectedPrereqCourse!: CourseInfoWithGradeRanges;
  public currentTargetCourse!: CourseCatalogAttributes;
  public targetCrsPassRate?: number;
  public selected!: string | undefined;

  data: CourseCatalogAttributes = {
    course_code: '',
    course_college: '',
    course_department: '',
    course_desc: '',
    course_name: '',
    course_hrs: '',
    course_prereqs: '',
  };

  displayedColumns: string[] = ['code', 'name', 'college', 'prereqs', 'action'];
  @ViewChildren('tableRow', { read: ElementRef })
  tableRows!: QueryList<ElementRef>;
  @ViewChild(PrerequisiteChartComponent) chart!: PrerequisiteChartComponent;

  constructor(
    private dataService: DataService,
    private snackbarService: SnackbarService,
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.dataService.getData().subscribe({
        next: (dataStream) => {
          if (dataStream.data) {
            const data = dataStream.data as TargetCourseCombined;
            this.targetCrsPassRate = data.target_course_pass_rate;
            this.insertRow(data);

            // Wait for the DOM to be updated with the initial row
            setTimeout(() => {
              this.tableRows.last.nativeElement.click();
            }, 100);
          }

          dataStream.error &&
            this.snackbarService.open(dataStream.error.message, 'error');
        },
      }),
    );

    this.subscription.add(
      this.dataService.getPendingData().subscribe((pending) => {
        this.dataPending = pending;
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private insertRow(targetCrs: TargetCourseCombined) {
    // Remove duplicates
    this.targetCrsTableData = this.targetCrsTableData.filter(
      (i) => i.target.course_code !== targetCrs.target.course_code,
    );
    this.table = this.table.filter(
      (row) => row.code !== targetCrs.target.course_code,
    );

    // Update target course table data and table rows
    this.targetCrsTableData.unshift(targetCrs);
    this.table.unshift(this.generateRow(targetCrs));
  }

  private generateRow(crs: TargetCourseCombined): Row {
    return {
      code: crs.target.course_code,
      name: crs.target.course_name,
      college: crs.target.course_college,
      prereqs: crs.target.course_prereqs,
    };
  }

  deleteRow(event: Event, row: Row) {
    event.stopPropagation();

    this.targetCrsTableData = this.targetCrsTableData.filter(
      (i) => i.target.course_code !== row.code,
    );
    this.table = this.table.filter((c) => c.code !== row.code);
  }

  // get course picked by user to show chart
  showChart(element: Row, prereq_course_code: string) {
    const selectedTargetCrs = this.targetCrsTableData.find(
      (c) => c.target.course_code === element.code,
    );
    this.selectedPrereqCourse = selectedTargetCrs!.prereqs.find(
      (c) => c.course_code === prereq_course_code,
    )!;

    this.currentTargetCourse = selectedTargetCrs?.target!;
  }

  // reset state of chart to have a default chart when user expands table row
  showDefaultChart(element: Row) {
    const selectedTargetCrs = this.targetCrsTableData.find(
      (c) => c.target.course_code === element.code,
    );
    this.selectedPrereqCourse = selectedTargetCrs!.prereqs[0];
    this.currentTargetCourse = selectedTargetCrs?.target!;
    this.selected =
      this.selectedPrereqCourse && this.selectedPrereqCourse.course_code;
  }
}
